import { useMsal } from "@azure/msal-react";
import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import CloudIcon from "@mui/icons-material/Cloud";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnvironmentViewModel, TenantViewModel, UserClient, UserViewModel } from "../../api/api";
import { createScopedLogger, getApplicationLogLevel, LogLevelName, LOG_LEVEL_OPTIONS, setApplicationLogLevel } from "../../common";
import { useApiClient } from "../../common/hooks";
import BundleContext from "../../UserInterfaceModule/Contexts/BundleContext";
import useMaskPiiSetting from "../../UserInterfaceModule/Hooks/useMaskPiiSetting";
import { IsDesignMode } from "../../UserInterfaceModule/Utils/runtimeDetection";
import AuthContext from "../AuthContext";
import AppSelectorDialog from "./AppSelectorDialog";
import EnvironmentSelectorDialog from "./EnvironmentSelectorDialog";
import TenantSelectorDialog from "./TenantSelectorDialog";

const logger = createScopedLogger("MenuListItemAvatar");

interface MenuListItemAvatarProps {
  open: boolean;
  additionalSx?: any;
}

export default function MenuListItemAvatar({ open, additionalSx }: MenuListItemAvatarProps) {
  const auth = useContext(AuthContext);
  const [maskPii, setMaskPii] = useMaskPiiSetting();
  const { accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [logLevel, setLogLevel] = useState<LogLevelName>(() => getApplicationLogLevel());
  const [userData, setUserData] = useState<UserViewModel | null>(null);
  const loadedAccountUsername = useRef<string | null>(null);
  const userClient = useApiClient(UserClient);
  const bundleContext = useContext(BundleContext);
  const showThemeSwitcher = bundleContext.Themes.length > 1;

  const account = useMemo(() => (accounts.length > 0 ? accounts[0] : undefined), [accounts]);

  // Load user data including current tenant and environment when account is available
  const loadUserData = useCallback(async () => {
    // Wait for MSAL to finish initialization and ensure we have an account
    if (!account || inProgress !== "none") {
      return;
    }

    // Do not load user data if not authenticated
    if (!auth.isAuthenticated) {
      return;
    }

    // Prevent loading if already loaded for this account
    if (loadedAccountUsername.current === account.username) {
      logger.debug("User data already loaded for account:", account.username);
      return;
    }

    // Set the flag immediately to prevent duplicate calls (especially in StrictMode).
    // We'll clear it below if we decide not to proceed.
    loadedAccountUsername.current = account.username;

    // Verify we can actually obtain a token before hitting the endpoint.
    // If the cached session is expired (no valid refresh token), getAccessToken()
    // returns undefined and we skip the call entirely — avoiding a 401 that would
    // otherwise trigger an unwanted sign-in redirect while on a public page.
    const token = await auth.authService.getAccessToken();
    if (!token) {
      logger.warn("No access token available, skipping user data load.");
      loadedAccountUsername.current = null;
      return;
    }

    try {
      logger.debug("Loading user data for account:", account.username);
      const user = await userClient.getUser();
      setUserData(user);
    } catch (error) {
      logger.error("Error loading user data:", error);
      // Reset the flag on error so it can retry later
      loadedAccountUsername.current = null;
    }
  }, [account, inProgress, userClient, auth.isAuthenticated, auth.authService]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignIn = async () => {
    handleClose();
    try {
      await auth.authService.signIn();
    } catch (error) {
      logger.error("Sign-in error:", error);
    }
  };

  const handleSignOut = async () => {
    handleClose();
    try {
      await auth.authService.signOut();
    } catch (error) {
      logger.error("Sign-out error:", error);
    }
  };

  const handleChangeTenant = () => {
    handleClose();
    setTenantDialogOpen(true);
  };

  const handleChangeEnvironment = () => {
    handleClose();
    setEnvironmentDialogOpen(true);
  };

  const handleChangeApp = () => {
    handleClose();
    setAppDialogOpen(true);
  };

  const handleLogLevelChange = (event: SelectChangeEvent) => {
    const nextLevel = event.target.value as LogLevelName;
    setLogLevel(nextLevel);
    setApplicationLogLevel(nextLevel);
  };

  const handleThemeChange = (event: SelectChangeEvent) => {
    bundleContext.SetSelectedThemeId(event.target.value);
  };

  const handleTenantChanged = async (tenant: TenantViewModel) => {
    // Update the user data with the new tenant
    if (userData) {
      setUserData({
        ...userData,
        tenant: tenant,
      });
    }

    // Reset loaded account username so user data will be reloaded
    loadedAccountUsername.current = null;

    // Reload user data to get fresh tenant information
    await loadUserData();

    // Refresh the bundle context to load data for the new tenant
    await bundleContext.Refresh();

    // Navigate to root to avoid using cached data from the previous tenant
    navigate("/");
  };

  const handleEnvironmentChanged = async (environment: EnvironmentViewModel) => {
    // Update the user data with the new environment
    if (userData) {
      setUserData({
        ...userData,
        environment: environment,
      });
    }

    // Reset loaded account username so user data will be reloaded
    loadedAccountUsername.current = null;

    // Reload user data to get fresh environment information
    await loadUserData();

    // Navigate to root to avoid using cached data from the previous environment
    navigate("/");
  };

  const handleAppChanged = async (appName: string) => {
    logger.debug("Changing app from", bundleContext.CurrentAppName, "to", appName);
    bundleContext.SetCurrentApp?.(appName);

    // Navigate to root to reload with the new app
    navigate("/");
  };

  const userName = auth.account?.name || auth.account?.displayName || "Guest";
  const initials = userName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={[
          {
            minHeight: 48,
            px: 2.5,
          },
          open
            ? {
                justifyContent: "initial",
              }
            : {
                justifyContent: "center",
              },
          additionalSx,
        ]}
      >
        <ListItemAvatar
          sx={[
            {
              minWidth: 0,
            },
            open
              ? {
                  mr: 2,
                }
              : {
                  mr: "auto",
                },
          ]}
        >
          <Avatar sx={{ width: 32, height: 32 }}>{initials}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="body2" noWrap>
              {userName}
            </Typography>
          }
          sx={[
            open
              ? {
                  opacity: 1,
                }
              : {
                  opacity: 0,
                },
          ]}
        />
      </ListItemButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {!auth.isAuthenticated ? (
          <MenuItem onClick={handleSignIn}>
            <ListItemIcon>
              <LoginIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign In</ListItemText>
          </MenuItem>
        ) : (
          <>
            <MenuItem key="info" onClick={handleClose} disabled>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={auth.account?.name || "Profile"}
                secondary={auth.account?.username}
                primaryTypographyProps={{ sx: { textAlign: "left" } }}
                secondaryTypographyProps={{ sx: { textAlign: "left" } }}
              />
            </MenuItem>
            <MenuItem key="sign-out" onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign Out</ListItemText>
            </MenuItem>
            {(userData?.tenant || (userData?.tenants && userData.tenants.length > 0)) && <Divider />}
            {userData?.tenant && (
              <MenuItem key="current-tenant" onClick={handleClose} disabled>
                <ListItemIcon>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Tenant: {userData.tenant.code}</ListItemText>
              </MenuItem>
            )}
            {userData?.tenants && userData.tenants.length > 0 && (
              <MenuItem key="change-tenant" onClick={handleChangeTenant}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Tenant</ListItemText>
              </MenuItem>
            )}
            {(userData?.environment || (userData?.environments && userData.environments.length > 0)) && <Divider />}
            {userData?.environment && (
              <MenuItem key="current-environment" onClick={handleClose} disabled>
                <ListItemIcon>
                  <CloudIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Environment: {userData.environment.code}</ListItemText>
              </MenuItem>
            )}
            {userData?.environments && userData.environments.length > 0 && (
              <MenuItem key="change-environment" onClick={handleChangeEnvironment}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Environment</ListItemText>
              </MenuItem>
            )}
            {(bundleContext.CurrentAppName || (userData?.appNames && userData.appNames.length > 1)) && <Divider />}
            {bundleContext.CurrentAppName && (
              <MenuItem key="current-app" onClick={handleClose} disabled>
                <ListItemIcon>
                  <ViewModuleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>App: {bundleContext.CurrentAppName}</ListItemText>
              </MenuItem>
            )}
            {userData?.appNames && userData.appNames.length > 1 && (
              <MenuItem key="change-app" onClick={handleChangeApp} disabled={IsDesignMode}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change App</ListItemText>
              </MenuItem>
            )}
          </>
        )}
        <Divider />
        <MenuItem role="menuitemcheckbox" aria-checked={maskPii} onClick={() => setMaskPii((value) => !value)}>
          <ListItemIcon>
            <PrivacyTipIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mask PII</ListItemText>
          {maskPii && <CheckIcon fontSize="small" color="primary" sx={{ ml: 2 }} />}
        </MenuItem>
        {showThemeSwitcher && (
          <>
            <Divider />
            <MenuItem key="theme" disableRipple sx={{ alignItems: "flex-start", gap: 1.5, py: 1.5 }}>
              <ListItemIcon sx={{ mt: 1 }}>
                <PaletteOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <FormControl size="small" variant="standard" sx={{ minWidth: 180 }}>
                <InputLabel id="app-theme-label">Theme</InputLabel>
                <Select
                  labelId="app-theme-label"
                  value={bundleContext.SelectedThemeId ?? ""}
                  onChange={handleThemeChange}
                  onClick={(event) => event.stopPropagation()}
                  label="Theme"
                >
                  {bundleContext.Themes.map((theme) => (
                    <MenuItem key={theme.Id} value={theme.Id}>
                      {theme.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem key="advanced-log-level" disableRipple sx={{ alignItems: "flex-start", gap: 1.5, py: 1.5 }}>
          <ListItemIcon sx={{ mt: 1 }}>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <FormControl size="small" variant="standard" sx={{ minWidth: 180 }}>
            <InputLabel id="runtime-log-level-label">Advanced Log Level</InputLabel>
            <Select
              labelId="runtime-log-level-label"
              value={logLevel}
              onChange={handleLogLevelChange}
              onClick={(event) => event.stopPropagation()}
              label="Advanced Log Level"
            >
              {LOG_LEVEL_OPTIONS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MenuItem>
      </Menu>
      <TenantSelectorDialog
        open={tenantDialogOpen}
        onClose={() => setTenantDialogOpen(false)}
        onTenantChanged={handleTenantChanged}
        availableTenants={userData?.tenants}
      />
      <EnvironmentSelectorDialog
        open={environmentDialogOpen}
        onClose={() => setEnvironmentDialogOpen(false)}
        onEnvironmentChanged={handleEnvironmentChanged}
        availableEnvironments={userData?.environments}
      />
      <AppSelectorDialog
        open={appDialogOpen}
        onClose={() => setAppDialogOpen(false)}
        onAppChanged={handleAppChanged}
        availableApps={userData?.appNames || []}
        currentAppName={bundleContext.CurrentAppName}
      />
    </>
  );
}
